import { useMemo } from "react";
import { PLH_UI_VISIBILITY } from "./plhTileInfo";

export interface TopBarTileMeta {
  currentWeek?: number;
  currentYear?: number;
  selectedWeek?: number;
  selectedYear?: number;
  selectedWeekStart?: string;
  selectedWeekEnd?: string;
  granularity?: "week" | "month";
  selectedMonth?: number | null;
  selectedCalendarYear?: number | null;
  selectedMonthStart?: string | null;
  selectedMonthEnd?: string | null;
}

export interface TopBarTileProps {
  meta: TopBarTileMeta | undefined;
  /** Current week param for API (e.g. "2026-W06"); null = backend default. */
  selectedWeekParam?: string | null;
  onWeekChange?: (week: string) => void;
  /** `YYYY-MM`; null = backend default (last completed month). */
  selectedMonthParam?: string | null;
  onMonthChange?: (month: string) => void;
  granularity?: "week" | "month";
  onGranularityChange?: (g: "week" | "month") => void;
  /** When true, parent shows AI Coach floating panel; button toggles it. */
  aiCoachOpen?: boolean;
  onAiCoachToggle?: () => void;
  theme?: "light" | "dark";
  onThemeToggle?: () => void;
  /** When set, shows a gear control to open Product-Led Health settings (e.g. warehouse materialization). */
  onOpenSettings?: () => void;
  /** When false, hides week/month controls and the date range (e.g. on Settings view). */
  showPeriodControls?: boolean;
}

/** Build YYYY-Www for a given week offset from current (0 = current week, 1 = last week, etc.). */
function getWeekParam(year: number, week: number): string {
  const w = String(week).padStart(2, "0");
  return `${year}-W${w}`;
}

function monthOptionsFromAnchor(y: number, m1to12: number, count: number) {
  const opts: { value: string; label: string }[] = [];
  let yv = y;
  let mv = m1to12;
  for (let i = 0; i < count; i += 1) {
    const value = `${yv}-${String(mv).padStart(2, "0")}`;
    const label = new Date(yv, mv - 1, 1).toLocaleString("default", {
      month: "short",
      year: "numeric",
    });
    opts.push({ value, label });
    mv -= 1;
    if (mv < 1) {
      mv = 12;
      yv -= 1;
    }
  }
  return opts;
}

export const TopBarTile = ({
  meta,
  selectedWeekParam = null,
  onWeekChange,
  selectedMonthParam = null,
  onMonthChange,
  granularity = "week",
  onGranularityChange,
  aiCoachOpen = false,
  onAiCoachToggle,
  theme = "light",
  onThemeToggle,
  onOpenSettings,
  showPeriodControls = true,
}: TopBarTileProps) => {
  const weekOptions = useMemo(() => {
    const year = meta?.currentYear ?? new Date().getFullYear();
    const cw = meta?.currentWeek ?? 1;
    const options: { value: string; label: string }[] = [];
    for (let i = 0; i < 6; i++) {
      let w = cw - i;
      let y = year;
      if (w < 1) {
        w += 52;
        y -= 1;
      }
      options.push({ value: getWeekParam(y, w), label: `W${w}` });
    }
    return options;
  }, [meta?.currentYear, meta?.currentWeek]);

  const monthOptions = useMemo(() => {
    const cy = meta?.selectedCalendarYear ?? new Date().getFullYear();
    const cm = meta?.selectedMonth ?? new Date().getMonth() + 1;
    return monthOptionsFromAnchor(cy, cm, 12);
  }, [meta?.selectedCalendarYear, meta?.selectedMonth]);

  const weekPill =
    meta?.selectedWeek != null
      ? `W${meta.selectedWeek}`
      : meta?.currentWeek != null
        ? `W${meta.currentWeek}`
        : weekOptions[0]?.label ?? null;

  const monthPill =
    meta?.selectedMonth != null && meta?.selectedCalendarYear != null
      ? new Date(meta.selectedCalendarYear, meta.selectedMonth - 1, 1).toLocaleString("default", {
          month: "short",
          year: "numeric",
        })
      : monthOptions[0]?.label ?? null;

  const dateRange =
    granularity === "month"
      ? meta?.selectedMonthStart && meta?.selectedMonthEnd
        ? `${meta.selectedMonthStart} – ${meta.selectedMonthEnd}`
        : "—"
      : meta?.selectedWeekStart && meta?.selectedWeekEnd
        ? `${meta.selectedWeekStart} – ${meta.selectedWeekEnd}`
        : "—";

  const handleWeekSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value) onWeekChange?.(value);
  };

  const handleMonthSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value) onMonthChange?.(value);
  };

  return (
    <div className="top-bar">
      <div className="top-left">
        {showPeriodControls && (
          <>
            {onGranularityChange && (
              <div className="plh-granularity-toggle" role="group" aria-label="Data aggregation">
                <button
                  type="button"
                  className={granularity === "week" ? "active" : ""}
                  onClick={() => onGranularityChange("week")}
                  aria-pressed={granularity === "week"}
                >
                  W
                </button>
                <button
                  type="button"
                  className={granularity === "month" ? "active" : ""}
                  onClick={() => onGranularityChange("month")}
                  aria-pressed={granularity === "month"}
                >
                  M
                </button>
              </div>
            )}
            {granularity === "week" && weekOptions.length > 0 && (
              <select
                className="wk-select"
                value={selectedWeekParam ?? weekOptions[0]?.value ?? ""}
                onChange={handleWeekSelect}
                aria-label="Select week"
              >
                {weekOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
            {granularity === "month" && monthOptions.length > 0 && (
              <select
                className="wk-select"
                value={selectedMonthParam ?? monthOptions[0]?.value ?? ""}
                onChange={handleMonthSelect}
                aria-label="Select month"
              >
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
            {granularity === "week" && weekPill != null && !weekOptions.length && (
              <span className="wk-pill">{weekPill}</span>
            )}
            {granularity === "month" && monthPill != null && !monthOptions.length && (
              <span className="wk-pill">{monthPill}</span>
            )}
          </>
        )}
      </div>
      <div className="top-right">
        {showPeriodControls && (
          <span className="wk-dates mono" title={granularity === "month" ? "Calendar month range" : "ISO week range"}>
            {dateRange}
          </span>
        )}
        {onThemeToggle && (
          <button
            type="button"
            className="theme-btn"
            onClick={onThemeToggle}
            aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
          >
            {theme === "light" ? "☽" : "☀"}
          </button>
        )}
        {onOpenSettings && (
          <button
            type="button"
            className="theme-btn"
            onClick={onOpenSettings}
            aria-label="Open settings"
            title="Settings"
          >
            ⚙
          </button>
        )}
        {PLH_UI_VISIBILITY["ai-coach-button"] !== false && (
          <button
            type="button"
            className="ai-btn"
            onClick={onAiCoachToggle ?? (() => {})}
            data-ai-coach-open={aiCoachOpen}
            aria-expanded={aiCoachOpen}
          >
            <span className="sparkle">✦</span> AI Coach
          </button>
        )}
      </div>
    </div>
  );
};
